import { useFeatureValue } from '@growthbook/growthbook-react';
import { FEATURE_FLAGS } from 'integrations/growthbook/growthbook';
import {
  EXPERIMENT_HOMEPAGE_HERO_SECTION,
  HomepageHeroVariant,
} from 'integrations/growthbook/features/experiments';
import classNames from 'classnames';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { StartNowButton } from './components/StartNowButton';

export const Section1 = () => {
  const t = useTranslations();

  // A/B Test variant for homepage hero section
  // GrowthBook is initialized synchronously with SSR features in _app.tsx
  const variant = useFeatureValue<HomepageHeroVariant>(
    FEATURE_FLAGS.EXPERIMENT_HOMEPAGE_HERO_SECTION,
    EXPERIMENT_HOMEPAGE_HERO_SECTION.VARIANTS.PRODUCT_PREVIEW,
  );

  const imgUrl = EXPERIMENT_HOMEPAGE_HERO_SECTION.ASSETS[variant];
  const isMascot = variant === EXPERIMENT_HOMEPAGE_HERO_SECTION.VARIANTS.MASCOT;

  return (
    <div className={styles.container}>
      <div className={styles.left.container}>
        <h1 className={styles.left.title}>
          {t.rich('pages.home.hero.title', {
            highlight: (chunks) => (
              <span className={styles.highlight}>{chunks}</span>
            ),
          })}
        </h1>
        <p className={styles.left.description}>
          {t('pages.home.hero.description')}
        </p>
        <p className={styles.left.tagline}>{t('pages.home.hero.tagline')}</p>
        <div className={styles.left.cta.wrapper}>
          <StartNowButton className={styles.left.cta.button} />
        </div>
      </div>
      <div className={styles.right.container}>
        <div
          className={
            isMascot
              ? styles.right.img.mascot.wrapper
              : styles.right.img.productPreview.wrapper
          }
        >
          <Image
            src={imgUrl}
            alt="MindFeed hero image"
            fill
            className={
              isMascot
                ? styles.right.img.mascot.image
                : styles.right.img.productPreview.image
            }
            // NextJS Image Optimisations at build time
            sizes={
              isMascot
                ? '440px' // Keep simple since mascot is always ~440px wide
                : '(max-width: 1024px) min(100vw, 497px), 872px'
            }
            priority
          />
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: classNames(
    'px-0 pt-8 pb-14 flex flex-col items-center lg:px-5 lg:pb-0 lg:flex-row lg:pt-14 max-w-7xl',
    'mx-auto overflow-visible sm:overflow-hidden lg:overflow-visible',
  ),
  left: {
    container: 'px-5 lg:min-w-[629px] mb-8 lg:px-0 lg:mb-0 lg:mr-20',
    title:
      'text-4xl font-bold text-mf-off-black-950 sm:leading-[57.28px] sm:text-5xl',
    description: 'mt-8 text-xl text-mf-off-black-500 leading-8',
    tagline: 'mt-8 text-xl font-semibold text-mf-off-black-800 leading-8',
    cta: {
      wrapper: 'mt-10',
      button: 'inline-block w-auto',
    },
  },
  right: {
    container:
      'w-full overflow-visible sm:overflow-hidden lg:overflow-visible px-6 lg:px-0',
    img: {
      mascot: {
        wrapper: classNames(
          // < lg
          'relative w-full max-w-[440px] aspect-[440/458]',
          // >= lg
          'mx-auto lg:max-w-[440px] lg:aspect-[440/458]',
        ),
        image: 'object-contain object-left',
      },
      productPreview: {
        wrapper: classNames(
          // < lg: be responsive with aspect ratio
          'relative max-w-[497px] aspect-[497/354]',
          // >= lg: take full width
          'mx-auto lg:w-[872px] lg:h-[620px] lg:aspect-auto lg:max-w-none',
        ),
        image: 'object-contain object-left',
      },
    },
  },
  highlight: 'text-mf-blue',
};
